/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import React, { useContext, useEffect, useState } from 'react';
import { Toggle as ToggleKit, FieldLayout } from '@veeam/components';
import hasIn from 'lodash/has';

import type { ToggleProps as ToggleKitProps } from '@veeam/components/lib/Toggle/types';

import { FormsContext } from '../Form';
import { ValidationStatus } from '../../enums';

import type { FormControlProps } from '../../interfaces';

export interface ToggleProps extends ToggleKitProps, Omit<FormControlProps, 'suffix'> {}

export const Toggle: React.FC<ToggleProps> = ({ label, inlineLabel = false, name, asTag, helpText, helpTextView, helpTextWrap, ...restProps }) => {
    const formsState = useContext(FormsContext);
    const { disabled } = restProps;

    const labelSuffix = restProps.labelSuffix === undefined ? ':' : restProps.labelSuffix;
    const labelText = label ? label + labelSuffix : label;

    const [value, setValue] = useState(formsState.getValue(name));
    const [touched, setTouched] = useState(false);

    const calcErrorText = () => {
        const validationStatus = formsState.validationState.getStatus();

        const error = formsState.getError(name);
        const showError = (touched || validationStatus !== ValidationStatus.Common) && error;

        return showError ? error : '';
    };

    const [errorText, setErrorText] = useState(() => calcErrorText());

    useEffect(() => {
        const updateValueFunction = () => {
            if (hasIn(formsState.value, name)) {
                setValue(formsState.getValue(name));
            }

            setErrorText(calcErrorText());
        };

        setErrorText(calcErrorText());

        formsState.onChange(updateValueFunction);
        return () => formsState.unsubscribeOnChange(updateValueFunction);
    }, [touched]);

    return (
        <FieldLayout
            label={labelText}
            error={errorText}
            disabled={disabled}
            asTag={asTag}
            inlineLabel={inlineLabel}
            helpText={helpText}
            helpTextView={helpTextView}
            helpTextWrap={helpTextWrap}
        >
            <div>
                <ToggleKit
                    {...restProps}
                    value={value}
                    onChange={(value) => {
                        formsState.setValue(name, value);
                        restProps.onChange && restProps.onChange(value as any);
                    }}
                    onBlur={() => setTouched(true)}
                />
            </div>
        </FieldLayout>
    );
};
